import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useSessionStorage } from "../../hooks/useSessionStorage";
import {
  type QuizWho,
  type QuizDietPreference,
  type QuizMetaEntrypoint,
  type QuizMetaQuestion,
  type QuizMetaAnswer,
  type QuizMetaEndState,
  ProductInterface,
} from "./types";
import { QuizResults } from "./QuizResults";
import { QuizWelcome } from "./QuizWelcome";
import { QuizStepWho } from "./QuizStepWho";
import { QuizStepName } from "./QuizStepName";
import { QuizStepDateOfBirth } from "./QuizStepDateOfBirth";
import { QuizStepVeganVegetarian } from "./QuizStepVeganVegetarian";
import { QuizProgress } from "./QuizProgress";
import { QuizPagination } from "./QuizPagination";
import { QuizStepStandard } from "./QuizStepStandard";

declare const Shopify: { asset_url?: string };

export interface QuizProps {
  entrypoints: QuizMetaEntrypoint[];
  questions: QuizMetaQuestion[];
  answers: QuizMetaAnswer[];
  endStates: QuizMetaEndState[];
}

export const Quiz: React.FC<QuizProps> = ({
  entrypoints,
  questions,
  answers,
  endStates,
}) => {
  const params = new URLSearchParams(window.location.search);

  // question stack is a list of all the questions that are asked or too be asked
  // it excludes the first question entrypoint.
  const [questionStack, setQuestionStack] = useSessionStorage<
    QuizMetaQuestion[]
  >("questionStack", []);

  const [who, setWho] = useSessionStorage<QuizWho | "">("who", "");
  const [name, setName] = useSessionStorage<string>("name", "");
  const [currentStepHandle, setCurrentStepHandle] = useSessionStorage<string>(
    "currentStepHandle",
    "who",
  );
  const [day, setDay] = useSessionStorage<string>("day", "");
  const [month, setMonth] = useSessionStorage<string>("month", "");
  const [year, setYear] = useSessionStorage<string>("year", "");
  const [selectedAnswers, setSelectedAnswers] = useSessionStorage<
    Record<string, string[]>
  >("selectedAnswers", {} as Record<string, string[]>);

  const [dietPreference, setDietPreference] = useSessionStorage<
    QuizDietPreference | ""
  >("dietPreference", "");

  const [error, setError] = useState<string | null>(null);
  const [finished, setFinished] = useState<boolean>(false);
  const [products, setProducts] = useSessionStorage<
    Record<string, ProductInterface[]>
  >("products", {} as Record<string, ProductInterface[]>);

  const [displayedEndState, setDisplayedEndState] =
    useState<QuizMetaEndState | null>(null);

  const assetUrl =
    (typeof Shopify !== "undefined" && Shopify?.asset_url) || "/";

  /**
   * Determines if we are showing a custom CMS created step.
   */
  const { customStep, customStepAnswers, isCustomStep, isEntrypoint } =
    useMemo((): {
      customStep: QuizMetaEntrypoint | QuizMetaQuestion | QuizMetaAnswer | null;
      customStepAnswers: QuizMetaAnswer[];
      isCustomStep: boolean;
      isEntrypoint: boolean;
    } => {
      let customStep:
        | QuizMetaEntrypoint
        | QuizMetaQuestion
        | QuizMetaAnswer
        | null = null;
      let customStepAnswers: QuizMetaAnswer[] = [];
      let isCustomStep = false;
      let isEntrypoint = false;

      const entrypoint = entrypoints.find(
        (entrypoint) => entrypoint.handle === currentStepHandle,
      );

      if (entrypoint) {
        customStep = entrypoint;
        customStepAnswers =
          entrypoint.answers?.map(
            (answer) =>
              answers.find((a) => a.handle === answer) as QuizMetaAnswer,
          ) ?? [];
        isCustomStep = true;
        isEntrypoint = true;
      } else {
        const question = questions.find(
          (question) => question.handle === currentStepHandle,
        );

        if (question) {
          customStep = question;
          isCustomStep = true;
          isEntrypoint = false;
          customStepAnswers =
            question.answers?.map(
              (answer) =>
                answers.find((a) => a.handle === answer) as QuizMetaAnswer,
            ) ?? [];
        }
      }

      return { customStep, customStepAnswers, isCustomStep, isEntrypoint };
    }, [questions, currentStepHandle, entrypoints]);

  const isFirstStep = currentStepHandle === "who";
  const isThirdPerson = who === "their" || who === "child";

  const progressPercent = useMemo(() => {
    if (currentStepHandle === "who") {
      return 10;
    }
    if (currentStepHandle === "name") {
      return 20;
    }
    if (currentStepHandle === "welcome") {
      return 30;
    }
    if (currentStepHandle === "date_of_birth") {
      return 40;
    }
    if (currentStepHandle === "vegan_vegetarian") {
      return 50;
    }

    const indexOfCurrentStep = questionStack.findIndex(
      (q) => q.handle === currentStepHandle,
    );

    return Math.min(
      90,
      60 + 10 * (indexOfCurrentStep / questionStack.length) * 100,
    );
  }, [questionStack, currentStepHandle]);

  const nameDisplay = name ? name.charAt(0).toUpperCase() + name.slice(1) : "";

  useEffect(() => {
    if (finished) {
      const content = document.querySelector(".quiz-content");
      if (content) {
        content.classList.add("completed");
      }

      const state = {
        event: "quizFinished",
        who,
        dietPreference,
        age: "",
      };

      try {
        const dateOfBirth = new Date(`${year}-${month}-${day}`);
        const age = new Date().getFullYear() - dateOfBirth.getFullYear();

        state.age = age.toString();
      } catch {
        // leave state.age as ""
      }

      // if finished is not in the URL params, add it
      if (!params.get("finished")) {
        params.set("finished", "true");
        params.delete("start");
        params.delete("clear");

        const dataLayer =
          (window as Window & { dataLayer?: unknown[] }).dataLayer ?? [];
        dataLayer.push(state);
        (window as Window & { dataLayer?: unknown[] }).dataLayer = dataLayer;

        window.history.replaceState(
          {},
          "",
          `${window.location.pathname}?${params}`,
        );
      }
    }

    return () => {
      params.delete("finished");
    };
  }, [who, dietPreference, year, month, day, finished]);

  /**
   * Returns the handle of the entrypoint that matches the current configuration (junior or not) handle.
   */
  const getEntrypoint = useCallback(() => {
    if (who === "child") {
      return (
        entrypoints.find((entrypoint) => entrypoint.junior === true) ?? null
      );
    }

    const adultEntrypoint =
      entrypoints.find(
        (entrypoint) =>
          typeof entrypoint.junior === "undefined" ||
          entrypoint.junior !== true,
      ) ?? null;

    return adultEntrypoint;
  }, [entrypoints, who]);

  /**
   * Handle all the logic for moving to the next step.
   *
   * 1. Check if there is an error.
   * 2. If the current step is the date of birth step, check if the date of birth is valid.
   * 3. If the current step is a custom step, check if the user has selected an answer.
   * 4. Lookup the next handle in the questionStack.
   * 5. Update the questionStack.
   * 6. Update the URL.
   */
  const goNext = () => {
    setError(null);

    let nextHandle: string | null = null;

    if (currentStepHandle === "who") {
      if (!who) {
        setError("Please select an option");
        return;
      }

      nextHandle = "name";
    } else if (currentStepHandle === "name") {
      if (!name || name === "") {
        if (isThirdPerson) {
          setError("Please enter their name");
          return;
        }
        setError("Please enter your name");
        return;
      }
      nextHandle = "welcome";
    } else if (currentStepHandle === "welcome") {
      nextHandle = "date_of_birth";
    } else if (currentStepHandle === "date_of_birth") {
      // validate the date of birth is 6+ years old and if not, set an error and return
      const dateOfBirth = new Date(`${year}-${month}-${day}`);
      const age = new Date().getFullYear() - dateOfBirth.getFullYear();
      if (age < 6) {
        setError("Our formulations have been developed for those aged 6+");
        return;
      }

      nextHandle = "vegan_vegetarian";
    } else if (currentStepHandle === "vegan_vegetarian") {
      const entrypoint = getEntrypoint();

      if (entrypoint) {
        nextHandle = entrypoint.handle;
      } else {
        console.error("No entrypoint found - missing entrypoint");
      }
    } else if (isCustomStep) {
      const hasSelectedAnAnswer =
        typeof selectedAnswers === "object"
          ? (selectedAnswers[customStep?.handle ?? ""] as unknown[])?.length > 0
          : false;

      if (!hasSelectedAnAnswer) {
        setError("Please select an answer");
        return;
      }

      const selectedAnswersForStep = selectedAnswers[
        customStep?.handle ?? ""
      ] as unknown as string[];

      const answerObjects = answers.filter((a) =>
        selectedAnswersForStep.includes(a.handle),
      );

      const nextQuestionHandles = answerObjects.map(
        (a) => a.nextQuestion?.reference ?? "",
      );

      const nextQuestions = questions.filter((q) =>
        nextQuestionHandles.includes(q.reference),
      );

      if (nextQuestions.length > 0) {
        // only add the next questions to the question stack if they are not already in the question stack
        const newQuestions = nextQuestions.filter(
          (q) => !questionStack.some((qs) => qs.reference === q.reference),
        );

        if (newQuestions.length > 0) {
          setQuestionStack([...questionStack, ...newQuestions]);
          nextHandle = newQuestions[0]?.handle ?? "";
        } else {
          nextHandle = nextQuestions[0]?.handle ?? "";
        }

        if (!nextHandle) {
          console.error("No next question handle found - missing nextQuestion");
        }
      } else if (answerObjects.length >= 1) {
        // we have at least one answer, so we need to check if it leads to an end state
        const answer = answerObjects[0];

        // check to see if we have any undefined answers
        let unfinishedQuestions = questionStack.filter(
          (q) => typeof selectedAnswers[q.handle] === "undefined",
        );

        console.log("unfinishedQuestions", unfinishedQuestions);

        if (unfinishedQuestions.length > 0) {
          nextHandle = unfinishedQuestions[0]?.handle ?? "";

          if (!nextHandle) {
            console.error(
              "No next unfinished question handle found - missing next question",
            );
          }

          // add any products to the products object
          if (answer.products?.length > 0) {
            setProducts((prev) => {
              const newProducts = { ...prev };
              newProducts[answer.handle] = answer.products;
              return newProducts;
            });
          } else {
            setProducts((prev) => {
              const newProducts = { ...prev };
              delete newProducts[answer.handle];
              return newProducts;
            });
          }
        } else if (answer?.matchToEndState) {
          // state matches an end state, so we need to set the displayed end state and finish the quiz.
          // i.e 'Cool as a cucumber'
          // only set the end state if we have matched no products
          let endState =
            endStates.find((e) => e.handle === answer.matchToEndState) ?? null;

          // remove any products  which may have been matched at this step
          setProducts((prev) => {
            const newProducts = { ...prev };
            delete newProducts[answer.handle];

            console.log(
              "clearing newProducts for answer step as we matched to an end state",
              answer.handle,
              newProducts,
            );

            return newProducts;
          });

          setDisplayedEndState(endState);
          setFinished(true);

          nextHandle = "finished";
        } else if (
          typeof answer?.products !== "undefined" &&
          answer?.products?.length > 0
        ) {
          setProducts((prev) => {
            const newProducts = { ...prev };
            newProducts[answer.handle] = answer.products;
            console.log("setting newProducts", newProducts);

            return newProducts;
          });

          if (!nextHandle) {
            nextHandle = "finished";

            setFinished(true);
          }
        } else {
          // remove the answer from the products
          setProducts((prev) => {
            const newProducts = { ...prev };
            delete newProducts[answer.handle];
            console.log(
              "clearing newProducts for answer step as we have no products",
              answer.handle,
              newProducts,
            );
            return newProducts;
          });
        }
      } else {
        nextHandle = customStep?.handle ?? "";

        if (!nextHandle) {
          console.error("No customStep question handle found - missing handle");
        }
      }
    }

    if (nextHandle) {
      setCurrentStepHandle(nextHandle);
    } else {
      console.error(
        "No next handle found - missing next question or end state",
      );
    }

    params.delete("start");
    params.delete("clear");
    params.set("start", "1");

    window.history.replaceState(
      null,
      "",
      `${window.location.pathname}?${params}`,
    );
  };

  console.log({ products });

  /**
   * User is going back in the process, either clearing the question stack, then going through the entrypoints
   * back to their user data steps.
   */
  const goPrev = () => {
    setError(null);

    let prevHandle: string | null = null;

    // remove the selectedAnswers
    setSelectedAnswers((prev) => {
      const newSelectedAnswers = { ...prev };
      delete newSelectedAnswers[currentStepHandle];
      return newSelectedAnswers;
    });

    if (questionStack.length <= 0) {
      if (currentStepHandle === "who") {
        prevHandle = "who";
      } else if (currentStepHandle === "name") {
        prevHandle = "who";
      } else if (currentStepHandle === "welcome") {
        prevHandle = "name";
      } else if (currentStepHandle === "date_of_birth") {
        prevHandle = "welcome";
      } else if (currentStepHandle === "vegan_vegetarian") {
        prevHandle = "date_of_birth";
      } else {
        prevHandle = "vegan_vegetarian";
      }
    } else {
      // take the end off the questionStack from the current step handle
      let indexOfCurrentStep = questionStack.findIndex(
        (q) => q.handle === currentStepHandle,
      );

      if (indexOfCurrentStep > 0) {
        prevHandle = questionStack[indexOfCurrentStep - 1]?.handle ?? "";
        const nextStack = questionStack.slice(0, indexOfCurrentStep);

        setQuestionStack(nextStack);
      } else if (indexOfCurrentStep === 0) {
        const entrypoint = getEntrypoint();

        if (entrypoint) {
          prevHandle = entrypoint.handle;
        } else {
          console.error("No entrypoint found - missing entrypoint");
        }

        // clear the question stack
        setQuestionStack([]);
      } else {
        // on the entry point so back to the vegan/vegetarian step
        prevHandle = "vegan_vegetarian";
        setQuestionStack([]);
      }
    }

    // remove any product matched at this step
    setProducts((prev) => {
      const newProducts = { ...prev };
      delete newProducts[currentStepHandle];
      return newProducts;
    });

    if (prevHandle) {
      setCurrentStepHandle(prevHandle);
    } else {
      console.error(
        "No previous handle found - missing previous question or end state",
      );
    }

    params.delete("finished");
    window.history.replaceState(
      null,
      "",
      `${window.location.pathname}?${params}`,
    );
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentStepHandle]);

  // listen to finished search param and set finished state if it is set
  useEffect(() => {
    if (params.get("finished") === "true") {
      setFinished(true);
    }
  }, [params]);

  return (
    <>
      <div className={`quiz-content${finished ? " completed" : ""}`}>
        <AnimatePresence mode="wait">
          {finished ? (
            <motion.div
              key="results"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <QuizResults
                name={name}
                isThirdPerson={isThirdPerson}
                products={Object.values(products).flat()}
                isVegan={dietPreference === "vegan"}
                endState={displayedEndState}
              />
            </motion.div>
          ) : (
            <motion.div
              key="steps"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <ol role="list" className="quiz-steps page-width">
                <AnimatePresence mode="wait">
                  {currentStepHandle === "who" && (
                    <QuizStepWho
                      key="who"
                      value={who}
                      onChange={(v) => setWho(v as QuizWho)}
                      error={error}
                      assetUrl={assetUrl}
                    />
                  )}

                  {currentStepHandle === "name" && (
                    <QuizStepName
                      key="name"
                      who={who as QuizWho}
                      value={name}
                      onChange={setName}
                      error={error}
                      onNext={goNext}
                    />
                  )}

                  {currentStepHandle === "welcome" && (
                    <QuizWelcome
                      key="welcome"
                      who={who as QuizWho}
                      nameDisplay={nameDisplay}
                      onNext={goNext}
                    />
                  )}

                  {currentStepHandle === "date_of_birth" && (
                    <QuizStepDateOfBirth
                      key="date_of_birth"
                      who={who as QuizWho}
                      day={day}
                      month={month}
                      year={year}
                      onDayChange={setDay}
                      onMonthChange={setMonth}
                      onYearChange={setYear}
                      error={error}
                    />
                  )}

                  {currentStepHandle === "vegan_vegetarian" && (
                    <QuizStepVeganVegetarian
                      key="vegan_vegetarian"
                      who={who as QuizWho}
                      value={dietPreference}
                      onChange={setDietPreference}
                      error={error}
                      assetUrl={assetUrl}
                      onNext={goNext}
                    />
                  )}

                  {customStep && (
                    <QuizStepStandard
                      key={customStep.handle}
                      step={customStep}
                      answers={customStepAnswers}
                      isThirdPerson={isThirdPerson}
                      isMultipleChoice={isEntrypoint}
                      selectedAnswers={selectedAnswers}
                      error={error}
                      setSelectedAnswers={(selected: string[]) => {
                        // remove duplicates
                        const uniqueSelected = [...new Set(selected)];

                        // answers should be saved by handle
                        setSelectedAnswers((prev) => ({
                          ...prev,
                          [customStep.handle]: uniqueSelected,
                        }));
                      }}
                    />
                  )}
                </AnimatePresence>
              </ol>

              <div className="quiz-footer">
                <QuizPagination
                  canGoPrev={!isFirstStep}
                  onPrev={goPrev}
                  onNext={goNext}
                />

                <QuizProgress percent={progressPercent} />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  );
};
